import {
  AccidentalDeath,
  type AccidentalDeathProductType,
} from "#src/types/products/AccidentalDeath.ts";
import {
  FinalExpense,
  type FinalExpenseProductType,
} from "#src/types/products/FinalExpense.ts";
import {
  IncomeRider,
  type IncomeRiderProductType,
} from "#src/types/products/IncomeRider.ts";
import {
  LinkedBenefit,
  type LinkedBenefitProductType,
} from "#src/types/products/LinkedBenefit.ts";
import {
  LongTermCare,
  type LongTermCareProductType,
} from "#src/types/products/LongTermCare.ts";
import {
  ReturnOfPremium,
  type ReturnOfPremiumProductType,
} from "#src/types/products/ReturnOfPremium.ts";
import {
  Term,
  NonMedQuoteParams,
  type TermProductType,
} from "#src/types/products/Term.ts";
import {
  AccumulationIUL,
  AllUniversalLife,
  ProtectionIUL,
  ProtectionVUL,
  UniversalLife,
  type UniversalLifeProductType,
} from "#src/types/products/UniversalLife.ts";
import {
  WholeLife,
  type WholeLifeProductType,
} from "#src/types/products/WholeLife.ts";

import {
  type ProductType,
  PRODUCT_TYPE,
  UL_TYPES,
} from "#src/constants/products.ts";
import { type ConceptType, CONCEPT_TYPE } from "#src/constants/concepts.ts";

export type Product =
  | AccidentalDeathProductType
  | FinalExpenseProductType
  | IncomeRiderProductType
  | LinkedBenefitProductType
  | LongTermCareProductType
  | ReturnOfPremiumProductType
  | TermProductType
  | UniversalLifeProductType
  | WholeLifeProductType;

export function getProduct(type: ProductType | ConceptType): Product {
  return {
    [PRODUCT_TYPE.INCOME_RIDER]: IncomeRider(),
    [PRODUCT_TYPE.FINAL_EXPENSE]: FinalExpense(),
    [PRODUCT_TYPE.ACCIDENTAL_DEATH]: AccidentalDeath(),
    [PRODUCT_TYPE.ACCUMULATION_IUL]: AccumulationIUL(),
    [PRODUCT_TYPE.PROTECTION_VUL]: ProtectionVUL(),
    [PRODUCT_TYPE.PROTECTION_IUL]: ProtectionIUL(),
    [PRODUCT_TYPE.UNIVERSAL_LIFE]: UniversalLife(),
    [PRODUCT_TYPE.LTC]: LongTermCare(),
    [PRODUCT_TYPE.ROP_TERM]: ReturnOfPremium(),
    [PRODUCT_TYPE.TERM]: Term(),
    [PRODUCT_TYPE.NON_MED]: NonMedQuoteParams(),
    [PRODUCT_TYPE.WHOLE_LIFE]: WholeLife(),
    [PRODUCT_TYPE.LINKED_BENEFIT]: LinkedBenefit(),
    [CONCEPT_TYPE.ALL_UL]: AllUniversalLife(),
  }[type];
}

export function getProductText(
  selectedType: ProductType | ConceptType,
  quoteCategories?: ProductType[]
) {
  if (!selectedType) return "";
  if (!quoteCategories) {
    const product = getProduct(selectedType);
    if (!product) return "";
    return product.display;
  }
  const ulType = UL_TYPES.find(t => quoteCategories.includes(t));

  if (selectedType !== CONCEPT_TYPE.ALL_UL || !ulType) {
    return getProduct(selectedType).display;
  }

  if (
    quoteCategories.includes(PRODUCT_TYPE.PROTECTION_VUL) &&
    quoteCategories.length > 1
  ) {
    return "Variable UL";
  }

  if (
    quoteCategories.includes(PRODUCT_TYPE.ACCUMULATION_IUL) &&
    quoteCategories.includes(PRODUCT_TYPE.PROTECTION_IUL)
  ) {
    return "Indexed UL";
  }

  return getProduct(ulType).display;
}
